/**
 * Done. / ChiroCPD — Account Page (/account)
 *
 * Sections:
 *   - Profile (email, display name, member since)
 *   - Subscription (status, next billing, cancel state)
 *   - Notification Preferences (driven by survey_data.reminders)
 *   - Your Data (download JSON export)
 *   - Delete Account (30-day grace period, confirms with modal)
 */

import { useEffect, useState } from 'react';
import { AppLayout } from '@/layouts/AppLayout';
import { api, ApiError } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import type { SurveyData } from '@/lib/types';

// ── Types ─────────────────────────────────────────────────────────────────────

interface AccountData {
  id: string;
  email: string;
  name: string | null;
  subscriptionStatus: string;
  subscriptionExpiresAt: string | null;
  createdAt: string;
  cohort: string | null;
  stripe: {
    interval: string | null;
    currentPeriodEnd: string | null;
    cancelAtPeriodEnd: boolean;
  } | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-NZ', { day: 'numeric', month: 'long', year: 'numeric' });
}

function planLabel(interval: string | null): string {
  if (interval === 'month') return 'Monthly plan';
  if (interval === 'year') return 'Annual plan';
  return 'Manual / custom';
}

function statusLabel(status: string): { label: string; cls: string } {
  switch (status) {
    case 'active':    return { label: 'Active',    cls: 'bg-green-100 text-green-800' };
    case 'past_due':  return { label: 'Past due',  cls: 'bg-yellow-100 text-yellow-800' };
    case 'cancelled': return { label: 'Cancelled', cls: 'bg-red-100 text-red-700' };
    default:          return { label: 'Inactive',  cls: 'bg-[var(--stitch-surface-container-high)] text-[var(--stitch-on-surface-variant)]' };
  }
}

// ── Section wrapper ────────────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }): React.ReactElement {
  return (
    <section className="rounded-xl border border-[var(--stitch-outline-variant)] bg-[var(--stitch-surface-container-lowest)] overflow-hidden">
      <div className="px-5 py-3 bg-[var(--stitch-surface-container-low)] border-b border-[var(--stitch-outline-variant)]">
        <h2 className="stitch-font-label text-[12px] font-semibold text-[var(--stitch-on-surface-variant)] uppercase tracking-wider">{title}</h2>
      </div>
      <div className="px-5 py-4">{children}</div>
    </section>
  );
}

// ── Notification Preferences ──────────────────────────────────────────────────

const REMINDER_OPTIONS = [
  { id: 'Gentle reminders',           label: 'Gentle reminders',           desc: 'Weekly email when your next CPD session is coming up.' },
  { id: 'Accountability prompts',     label: 'Accountability prompts',     desc: 'Nudge if you haven\'t logged any CPD in 14 days.' },
  { id: 'Progress tracking',          label: 'Progress tracking',          desc: 'Monthly hours-vs-target summary.' },
  { id: 'Peer discussion invitations',label: 'Peer discussion invitations', desc: 'Coming soon — invitations to group CPD discussions.' },
];

function NotificationPreferences(): React.ReactElement {
  const [reminders, setReminders] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const data = await api.get<SurveyData | null>('/api/auth/survey-data');
        setReminders(data?.reminders ?? []);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  function toggle(id: string): void {
    setReminders(prev => prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]);
    setSaved(false);
  }

  async function save(): Promise<void> {
    setSaving(true);
    try {
      await api.put('/api/auth/notification-preferences', { reminders });
      setSaved(true);
      setTimeout(() => { setSaved(false); }, 2500);
    } catch { /* ignore */ }
    finally { setSaving(false); }
  }

  if (loading) return <p className="text-sm text-[var(--stitch-on-surface-variant)]">Loading…</p>;

  return (
    <div className="flex flex-col gap-4">
      <div className="space-y-3">
        {REMINDER_OPTIONS.map(opt => (
          <label key={opt.id} className="flex items-start gap-3 cursor-pointer group">
            <input
              type="checkbox"
              checked={reminders.includes(opt.id)}
              onChange={() => { toggle(opt.id); }}
              className="mt-0.5 h-4 w-4 rounded border-[var(--stitch-outline-variant)] text-[var(--stitch-primary)] focus:ring-[var(--stitch-primary)]"
            />
            <div>
              <p className="text-sm font-medium text-[var(--stitch-on-surface)]">{opt.label}</p>
              <p className="text-xs text-[var(--stitch-on-surface-variant)]">{opt.desc}</p>
            </div>
          </label>
        ))}
      </div>
      <div className="flex items-center gap-3">
        <Button variant="secondary" size="sm" onClick={() => { void save(); }} ariaDisabled={saving}>
          {saving ? 'Saving…' : 'Save preferences'}
        </Button>
        {saved && <span className="text-xs text-green-700">Saved.</span>}
      </div>
    </div>
  );
}

// ── Delete Account Modal ──────────────────────────────────────────────────────

function DeleteModal({ onConfirm, onCancel, busy }: {
  onConfirm: () => void;
  onCancel: () => void;
  busy: boolean;
}): React.ReactElement {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-md rounded-xl bg-white shadow-xl p-6 space-y-4">
        <h3 className="text-lg font-bold text-[var(--stitch-on-surface)]">Delete your account?</h3>
        <div className="text-sm text-[var(--stitch-on-surface-variant)] space-y-2">
          <p>Here's what happens:</p>
          <ul className="list-disc list-inside space-y-1 ml-1">
            <li>Your Stripe subscription will be cancelled at the end of your current billing period.</li>
            <li>Your account will be anonymised in <strong>30 days</strong>.</li>
            <li>You can log back in and cancel at any time before then.</li>
            <li>Your CPD activity records are kept in anonymised form for aggregate statistics.</li>
          </ul>
          <p className="pt-1">We'll email you a confirmation and a link to download your data.</p>
        </div>
        <div className="flex gap-3 justify-end pt-2">
          <Button variant="secondary" size="sm" onClick={onCancel} ariaDisabled={busy}>
            Keep my account
          </Button>
          <button
            type="button"
            disabled={busy}
            onClick={onConfirm}
            className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
          >
            {busy ? 'Scheduling…' : 'Yes, delete my account'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main content ──────────────────────────────────────────────────────────────

function AccountContent(): React.ReactElement {
  const [account, setAccount] = useState<AccountData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);

  const [deletionScheduledAt, setDeletionScheduledAt] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const data = await api.get<AccountData>('/api/auth/account');
        setAccount(data);
        setName(data.name ?? '');
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return;
        setError(err instanceof Error ? err.message : 'Failed to load account.');
      }
    })();
    // Check if deletion is already scheduled
    void (async () => {
      try {
        const status = await api.get<{ deletionScheduledAt: string | null }>('/api/auth/deletion-status');
        setDeletionScheduledAt(status.deletionScheduledAt);
      } catch { /* ignore */ }
    })();
  }, []);

  const handleSaveName = async (): Promise<void> => {
    if (!name.trim()) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      await api.put('/api/auth/account/name', { name: name.trim() });
      setSaveMsg('Name updated.');
      if (account) setAccount({ ...account, name: name.trim() });
    } catch (err) {
      setSaveMsg(err instanceof Error ? err.message : 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async (): Promise<void> => {
    setDownloading(true);
    try {
      const data = await api.get<unknown>('/api/auth/export-data');
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `done-cpd-data-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { /* ignore */ }
    finally { setDownloading(false); }
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    setDeleteBusy(true);
    try {
      const result = await api.post<{ data: { deletionScheduledAt: string } }>('/api/auth/delete-account', {});
      setDeletionScheduledAt(result.data.deletionScheduledAt);
      setShowDeleteModal(false);
      // User is now logged out — redirect to home
      window.location.href = '/';
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to schedule deletion.');
      setShowDeleteModal(false);
    } finally {
      setDeleteBusy(false);
    }
  };

  const handleCancelDeletion = async (): Promise<void> => {
    setCancelBusy(true);
    try {
      await api.delete('/api/auth/cancel-deletion');
      setDeletionScheduledAt(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel deletion.');
    } finally {
      setCancelBusy(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <header className="mb-8">
        <h1 className="stitch-font-display text-[30px] md:text-[34px] font-bold text-[var(--stitch-primary)] tracking-tight">
          Account
        </h1>
      </header>

      {error && (
        <div role="alert" className="mb-6 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {/* Pending deletion banner */}
      {deletionScheduledAt && (
        <div role="alert" className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-red-800">Account deletion scheduled</p>
            <p className="text-xs text-red-700 mt-0.5">
              Your account will be anonymised on {formatDate(deletionScheduledAt)}. Log in before then to cancel.
            </p>
          </div>
          <button
            type="button"
            disabled={cancelBusy}
            onClick={() => { void handleCancelDeletion(); }}
            className="shrink-0 px-3 py-1.5 rounded-lg border border-red-300 bg-white text-sm font-medium text-red-700 hover:bg-red-50 transition-colors disabled:opacity-50"
          >
            {cancelBusy ? 'Cancelling…' : 'Cancel deletion'}
          </button>
        </div>
      )}

      {!account && !error && (
        <div className="flex items-center justify-center py-24">
          <span className="material-symbols-outlined animate-spin text-[var(--stitch-primary)] text-4xl">progress_activity</span>
        </div>
      )}

      {account && (
        <div className="flex flex-col gap-6">

          {/* Profile */}
          <Section title="Profile">
            <div className="flex flex-col gap-4">
              <div>
                <label className="block text-xs font-medium text-[var(--stitch-on-surface-variant)] mb-1">Email</label>
                <p className="text-sm text-[var(--stitch-on-surface)]">{account.email}</p>
              </div>
              <div>
                <label htmlFor="display-name" className="block text-xs font-medium text-[var(--stitch-on-surface-variant)] mb-1">Display name</label>
                <div className="flex items-center gap-2">
                  <input
                    id="display-name"
                    type="text"
                    value={name}
                    onChange={e => { setName(e.target.value); setSaveMsg(null); }}
                    onKeyDown={e => { if (e.key === 'Enter') void handleSaveName(); }}
                    className="flex-1 px-3 py-1.5 rounded-lg border border-[var(--stitch-outline-variant)] text-sm bg-white focus:outline-none focus:border-[var(--stitch-primary)]"
                  />
                  <Button variant="secondary" size="sm" onClick={() => { void handleSaveName(); }} ariaDisabled={saving}>
                    {saving ? 'Saving…' : 'Save'}
                  </Button>
                </div>
                {saveMsg && <p className="mt-1 text-xs text-green-700">{saveMsg}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--stitch-on-surface-variant)] mb-1">Member since</label>
                <p className="text-sm text-[var(--stitch-on-surface)]">{formatDate(account.createdAt)}</p>
              </div>
            </div>
          </Section>

          {/* Subscription */}
          <Section title="Subscription">
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <span className={`inline-flex px-2.5 py-0.5 rounded-full stitch-font-label text-[11px] font-semibold ${statusLabel(account.subscriptionStatus).cls}`}>
                  {statusLabel(account.subscriptionStatus).label}
                </span>
                {account.stripe && (
                  <span className="text-sm text-[var(--stitch-on-surface-variant)]">
                    {planLabel(account.stripe.interval)}
                  </span>
                )}
              </div>
              {account.stripe?.currentPeriodEnd && (
                <div>
                  <label className="block text-xs font-medium text-[var(--stitch-on-surface-variant)] mb-0.5">
                    {account.stripe.cancelAtPeriodEnd ? 'Access until' : 'Next billing date'}
                  </label>
                  <p className="text-sm text-[var(--stitch-on-surface)]">{formatDate(account.stripe.currentPeriodEnd)}</p>
                  {account.stripe.cancelAtPeriodEnd && (
                    <p className="text-xs text-yellow-700 mt-0.5">Subscription cancelled — access continues until this date.</p>
                  )}
                </div>
              )}
              {!account.stripe && account.subscriptionExpiresAt && (
                <div>
                  <label className="block text-xs font-medium text-[var(--stitch-on-surface-variant)] mb-0.5">Access until</label>
                  <p className="text-sm text-[var(--stitch-on-surface)]">{formatDate(account.subscriptionExpiresAt)}</p>
                </div>
              )}
              {!account.stripe && !account.subscriptionExpiresAt && (
                <p className="text-sm text-[var(--stitch-on-surface-variant)]">No billing details on file.</p>
              )}
            </div>
          </Section>

          {/* Notification preferences */}
          <Section title="Email Notifications">
            <NotificationPreferences />
          </Section>

          {/* Data export */}
          <Section title="Your Data">
            <div className="flex flex-col gap-3">
              <p className="text-sm text-[var(--stitch-on-surface-variant)]">
                Download a copy of all your Done. CPD data — profile, survey answers, activities, reflections, annual plans, and certificates.
              </p>
              <div>
                <button
                  type="button"
                  onClick={() => { void handleDownload(); }}
                  disabled={downloading}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[var(--stitch-outline-variant)] bg-white hover:bg-[var(--stitch-surface-container-low)] text-sm font-medium text-[var(--stitch-on-surface)] transition-colors disabled:opacity-50"
                >
                  <span className="material-symbols-outlined text-[16px]">download</span>
                  {downloading ? 'Preparing…' : 'Download my data (JSON)'}
                </button>
              </div>
            </div>
          </Section>

          {/* Delete account */}
          {!deletionScheduledAt && (
            <Section title="Delete Account">
              <div className="flex flex-col gap-3">
                <p className="text-sm text-[var(--stitch-on-surface-variant)]">
                  Requesting deletion will cancel your subscription at the end of your billing period and anonymise your personal data after 30 days. You can cancel within the 30-day window.
                </p>
                <div>
                  <button
                    type="button"
                    onClick={() => { setShowDeleteModal(true); }}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-red-200 bg-white hover:bg-red-50 text-sm font-medium text-red-600 transition-colors"
                  >
                    <span className="material-symbols-outlined text-[16px]">delete_forever</span>
                    Request account deletion
                  </button>
                </div>
              </div>
            </Section>
          )}

        </div>
      )}

      {showDeleteModal && (
        <DeleteModal
          onConfirm={() => { void handleDeleteConfirm(); }}
          onCancel={() => { setShowDeleteModal(false); }}
          busy={deleteBusy}
        />
      )}
    </div>
  );
}

export function AccountPage(): React.ReactElement {
  return (
    <AppLayout currentRoute="/account">
      <AccountContent />
    </AppLayout>
  );
}
